import React, { useCallback, useContext, useState } from "react";
import { InfoBanner, TableHeader, WithRoleAccess } from "@services/ui-components";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useReactiveVar } from "@apollo/client";
import OnlineBookingSettingsTable from "./OnlineBookingSettingsTable";
import Button from "react-bootstrap/Button";
import FacebookPixel from "./FacebookPixel/FacebookPixel";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { useParams } from "react-router-dom";
import { AccessContext } from "@me-team/host/src/components/App/App";
import AccessService from "@me-team/host/services/AccessService";
import { CompanyAccess } from "@me-team/host/main/graphql/types";
import { useRoleAccess, useRoleAccessAny } from "@me-team/host/src/utils/useRoleAccess";
import Spinner from "@services/ui-components/src/Spinner/Spinner";

const OnlineBookingSettings: React.FC = () => {
    const { t } = useTranslation();
    const { branchId } = useParams();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const { isReady: isAccessContextReady } = useContext(AccessContext);
    const accessService = AccessService.getInstance();
    const [isPixelModalOpen, setIsPixelModalOpen] = useState<boolean>(false);
    const canViewOnlineBookingSettings = useRoleAccessAny([
        CompanyAccess.OnlineBookingSettingsView,
        CompanyAccess.OnlineBookingSettingsEdit,
    ]);
    const canEditOnlineBookingSettings = useRoleAccess(CompanyAccess.OnlineBookingSettingsEdit);
    const isOnlineBookingSettingsAccessPending =
        !isAccessContextReady || !accessService.isRoleAccessReady() || accessService.isRoleAccessLoading();
    const isReadOnly = canViewOnlineBookingSettings && !canEditOnlineBookingSettings;

    const onOpenPixelModalHandler = () => {
        setIsPixelModalOpen(true);
    };
    const onClosePixelModalHandler = useCallback(() => {
        setIsPixelModalOpen(false);
    }, []);

    if (isOnlineBookingSettingsAccessPending) {
        return <Spinner />;
    }

    return (
        <div className="mb-5 px-2 px-xl-0 pb-5">
            <InfoBanner>
                <p className="fs-7 mb-0">
                    {t(
                        "On this page, you can enable or disable online registration for your company as a whole, or set it up separately for each of the branches, as well as copy the URL or download the QR code to post it for your customers."
                    )}
                </p>
            </InfoBanner>
            <TableHeader
                title={t("Online Booking Settings")}
                paths={[
                    { path: `/booking/company/${currentCompanyId}/config/${branchId}`, label: t("Online Booking") },
                    {
                        path: `/booking/company/${currentCompanyId}/config/${branchId}`,
                        label: t("Online Booking Settings"),
                    },
                ]}
                colSize={7}
                btnColSize={5}
                buttons={
                    <WithRoleAccess permission={CompanyAccess.OnlineBookingSettingsEdit}>
                        <Row className="gx-3 justify-content-end mt-4 mb-2">
                            <Col xs={12} sm={12} lg={7} xl={6} xxl={5}>
                                <Button
                                    className="btn btn-primary w-100 fw-bold px-2 text-truncate"
                                    onClick={onOpenPixelModalHandler}
                                >
                                    {t("Facebook Pixel Code")}
                                </Button>
                            </Col>
                        </Row>
                    </WithRoleAccess>
                }
            ></TableHeader>
            <Row>
                <Col>
                    <span className="fs-4">{t("Main Settings")}</span>
                </Col>
            </Row>
            <OnlineBookingSettingsTable canEditOnlineBookingSettings={!isReadOnly} />

            {isPixelModalOpen && (
                <FacebookPixel
                    isPixelModalOpen={isPixelModalOpen}
                    onClose={onClosePixelModalHandler}
                    canEditOnlineBookingSettings={canEditOnlineBookingSettings}
                />
            )}
        </div>
    );
};

export default OnlineBookingSettings;
